import type { FinancialNumber } from 'financial-number'
import { for_each, map } from '#shared/array.ts'
import assert from '#shared/assert.ts'
import { omit } from '#shared/omit.ts'
import { query_requires_transaction } from './query_requires_transaction.ts'
import type {
	Comparator,
	ArrayComparator,
	SingleValue,
	SafeSelectQuery,
	Comparison,
	ColumnReference,
	UserProvidedValue,
	FunctionExpression,
	FunctionName,
	Join as TrustableJoin,
	AliasReference,
	OrderBy,
	OrderByDirection,
	OrderByExpression,
	HavingComparison,
	HavingGrouping,
	SelectExpression,
	SelectGrouping,
	WhereGrouping,
	TableSource,
} from "./safe_select_query_validator.ts"

type SchemaColumnTypes = {
	[table_name in string]: {
		[column_name in string]: any
	}
}

type AliasMap<Schema extends SchemaColumnTypes> = {
	[alias: string]: Extract<keyof Schema, string>
}

type ColumnRef<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> = {
	[Alias in keyof A & string]: `${Alias}.${Extract<keyof Schema[A[Alias]], string>}`
}[keyof A & string]

type ValueRef = { value: SingleValue }

type ArrayValues = readonly (string | bigint)[]

type Expression = { __expression: true }

type ExpressionBuilder<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> = {
	comparison: (
		left: ColumnRef<Schema, A> | ValueRef | FunctionExpression,
		comparator: Comparator,
		right: ColumnRef<Schema, A> | ValueRef | FunctionExpression,
	) => Expression
	in: (left: ColumnRef<Schema, A> | FunctionExpression, values: ArrayValues) => Expression
	not_in: (left: ColumnRef<Schema, A> | FunctionExpression, values: ArrayValues) => Expression
	and: (...exprs: Expression[]) => Expression
	or: (...exprs: Expression[]) => Expression
	fn: (name: FunctionName, ...args: (ColumnRef<Schema, A> | ValueRef)[]) => FunctionExpression
}

// HAVING filters on the aggregated result, so its comparison operands are select identifiers (`Ids`)
// or values — never raw table columns.
type HavingExpressionBuilder<Ids extends string> = {
	comparison: (left: Ids | ValueRef, comparator: Comparator, right: Ids | ValueRef) => Expression
	and: (...exprs: Expression[]) => Expression
	or: (...exprs: Expression[]) => Expression
}

type SelectColumnInput<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> =
	ColumnRef<Schema, A> | `${ColumnRef<Schema, A>} AS ${string}`

type FunctionReturnType<Fn extends FunctionName> =
	Fn extends 'COUNT' | 'COUNT DISTINCT' ? bigint
	: Fn extends 'IS NULL' | 'IS NOT NULL' ? boolean
	: unknown

type ColumnTypeOf<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>, CR> =
	CR extends `${infer T}.${infer C}`
		? T extends keyof A
			? C extends keyof Schema[A[T] & keyof Schema]
				? Schema[A[T] & keyof Schema][C]
				: never
			: never
		: never

// The claimed return types are assertions about the connection's typeCast, like the ones in
// mysql_function.ts. MAX/MIN return the column's own MySQL type. SUM/AVG return NEWDECIMAL,
// which typeCast maps to FinancialNumber. All aggregates return NULL when no rows match.
type FunctionReturnTypeWithArg<Fn extends FunctionName, Arg> =
	Fn extends 'MAX' | 'MIN' ? Arg | null
	: Fn extends 'SUM' | 'AVG' ? FinancialNumber | null
	: FunctionReturnType<Fn>

type FunctionReturnTypeWithTwoArgs<Fn extends FunctionName, Arg1, Arg2> =
	Fn extends 'IFNULL' ? Exclude<Arg1, null> | Arg2
	: Fn extends 'GREATEST' ? Arg1 | Arg2
	: FunctionReturnType<Fn>

declare const function_return_type: unique symbol

// function_return_type is a phantom key recording the TS type the function produces in the row,
// so RowEntry can recover it without re-deriving it from the function name and arguments.
export type SelectableFunctionExpression<
	Table extends string = string,
	Alias extends string = string,
	Fn extends FunctionName = FunctionName,
	Return = unknown,
> = FunctionExpression & {
	function: Fn
	table_identifier: Table
	alias: Alias
	readonly [function_return_type]?: Return
}

type BeforeDot<S> = S extends `${infer T extends string}.${string}` ? T : never
type AfterDot<S> = S extends `${string}.${infer A extends string}` ? A : never

type SelectFnAlias<A extends AliasMap<any>> = `${keyof A & string}.${string}`

type SelectGroupingItem<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> =
	SelectColumnInput<Schema, A> | SelectGrouping

type SelectFnArg<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> = ColumnRef<Schema, A> | ValueRef

type SelectFnArgType<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>, Arg> =
	Arg extends { value: infer V } ? V : ColumnTypeOf<Schema, A, Arg>

type SelectExpressionBuilder<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> = {
	fn: {
		<const Fn extends FunctionName, const Alias extends SelectFnAlias<A>>(
			name: Fn,
			alias: Alias,
		): SelectableFunctionExpression<BeforeDot<Alias>, AfterDot<Alias>, Fn, FunctionReturnType<Fn>>
		<const Fn extends FunctionName, const Arg extends SelectFnArg<Schema, A>, const Alias extends SelectFnAlias<A>>(
			name: Fn,
			arg: Arg,
			alias: Alias,
		): SelectableFunctionExpression<BeforeDot<Alias>, AfterDot<Alias>, Fn, FunctionReturnTypeWithArg<Fn, SelectFnArgType<Schema, A, Arg>>>
		<const Fn extends FunctionName, const Arg1 extends SelectFnArg<Schema, A>, const Arg2 extends SelectFnArg<Schema, A>, const Alias extends SelectFnAlias<A>>(
			name: Fn,
			arg1: Arg1,
			arg2: Arg2,
			alias: Alias,
		): SelectableFunctionExpression<BeforeDot<Alias>, AfterDot<Alias>, Fn, FunctionReturnTypeWithTwoArgs<Fn, SelectFnArgType<Schema, A, Arg1>, SelectFnArgType<Schema, A, Arg2>>>
	}
	and: <const Alias extends SelectFnAlias<A>>(...items: [...SelectGroupingItem<Schema, A>[], Alias]) => SelectGrouping
	or: <const Alias extends SelectFnAlias<A>>(...items: [...SelectGroupingItem<Schema, A>[], Alias]) => SelectGrouping
}

type SelectInput<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>> =
	| SelectColumnInput<Schema, A>
	| SelectableFunctionExpression
	| SelectGrouping

type RowEntry<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>, Expr> =
	Expr extends { type: 'function'; table_identifier: infer T extends string; alias: infer K extends string; readonly [function_return_type]?: infer R }
		? T extends keyof A
			? { [_ in T]: { [_ in K]: R } }
			: never
		: Expr extends `${infer T}.${infer C} AS ${infer K}`
			? T extends keyof A
				? C extends keyof Schema[A[T] & keyof Schema]
					? { [_ in T]: { [_ in K]: Schema[A[T] & keyof Schema][C] } }
					: never
				: never
			: Expr extends `${infer T}.${infer C}`
				? T extends keyof A
					? C extends keyof Schema[A[T] & keyof Schema]
						? { [_ in T]: { [_ in C]: Schema[A[T] & keyof Schema][C] } }
						: never
					: never
				: never

type UnionToIntersection<U> =
	(U extends any ? (x: U) => void : never) extends (x: infer I) => void ? I : never

type RowFromSelectExprs<Schema extends SchemaColumnTypes, A extends AliasMap<Schema>, Exprs extends ReadonlyArray<unknown>> =
	UnionToIntersection<{ [I in keyof Exprs]: RowEntry<Schema, A, Exprs[I]> }[number]>

// Each selected column contributes a `{ table: { column: type } }` entry, and they get intersected
// into `{ table: { a } } & { table: { b } } & ...`. Re-mapping both levels collapses those `&`s into
// a single `{ table: { a; b; ... } }` object. Indexed access (`Row[Table][Column]`) is used on the
// inner level so column value types (bigint, Temporal.Instant, ...) are preserved, not expanded.
// A left-joined table either matched (every column keeps its schema type) or matched nothing (the
// runtime fills every selected column with null), so its object is a union of those two shapes
// rather than `| null` on each column — that keeps schema nullability recoverable after a
// null-key check proves the join matched (see group_joined_rows.ts).
type FlattenRow<Row, LeftJoined extends string = never> = {
	[Table in keyof Row]: Table extends LeftJoined
		? { [Column in keyof Row[Table]]: Row[Table][Column] } | { [Column in keyof Row[Table]]: null }
		: { [Column in keyof Row[Table]]: Row[Table][Column] }
}

export type ResponseColumn = {
	table_identifier: string
	name: string
}

export type BuiltQuery<Row> = {
	query: SafeSelectQuery
	response_columns: ResponseColumn[]
	positional_row_to_named: (row: unknown[]) => Row
}

// `build()` already flattened the row, so re-mapping here would collapse a left-joined table's
// matched-or-all-null union back into `| null` on each column.
export type ExtractQueryResponse<T> = T extends BuiltQuery<infer Row>
	? Row
	: never

// Given a flattened `{ table: { identifier: type } }` row, the union of all identifiers (the inner keys).
type IdentifiersOfFlatRow<Flat> = {
	[Table in keyof Flat]: keyof Flat[Table] & string
}[keyof Flat]

// Internal: the identifiers produced by SELECT so far (column aliases, function aliases, grouping
// aliases), derived from the accumulated `Row` type. `never` until something has been selected.
type RowIdentifiers<Row> = IdentifiersOfFlatRow<FlattenRow<Row>>

// Public: the union of identifiers a built query selects — whether from a column, a column alias, a
// function alias, or a grouping alias. Namespacing-by-table is collapsed; if two tables select the
// same identifier you get it once. Use `ExtractQueryResponse` if you need to keep the table grouping.
export type SelectedIdentifiers<Q> = IdentifiersOfFlatRow<ExtractQueryResponse<Q>>

type TableAliasArg<Schema extends SchemaColumnTypes> =
	Extract<keyof Schema, string> | `${Extract<keyof Schema, string>} AS ${string}`

type ParseTableAlias<S extends string, Schema extends SchemaColumnTypes> =
	S extends `${infer T extends Extract<keyof Schema, string>} AS ${infer A}`
		? { [K in A]: T }
		: S extends Extract<keyof Schema, string>
			? { [K in S]: S }
			: never

// A built query used as a derived table exposes its selected identifiers as the columns of one table.
// The per-table namespacing collapses, the same way it does for `SelectedIdentifiers`.
type DerivedColumns<Row> = UnionToIntersection<{
	[Table in keyof Row]: { [Column in keyof Row[Table]]: Row[Table][Column] }
}[keyof Row]>

type DerivedTableArg<Alias extends string, Row> = {
	subquery: BuiltQuery<Row>
	alias: Alias
}

type DerivedSchema<Schema extends SchemaColumnTypes, Alias extends string, Row> =
	Omit<Schema, Alias> & { [K in Alias]: DerivedColumns<Row> }

// Intersecting each alias target with `keyof NewSchema` lets a generic alias map satisfy AliasMap for
// the new schema. For concrete types the intersection resolves to the alias target itself.
type RebindAliasMap<A, NewSchema extends SchemaColumnTypes> = {
	[K in keyof A]: A[K] & Extract<keyof NewSchema, string>
}

type DerivedAliasMap<Schema extends SchemaColumnTypes, A, Alias extends string, Row> =
	RebindAliasMap<A & { [K in Alias]: K }, DerivedSchema<Schema, Alias, Row>>

type Joiner<
	Schema extends SchemaColumnTypes,
	A extends AliasMap<Schema>,
	LeftJoined extends string,
	Row,
	Left extends boolean,
	AllowsTransactionRequiredQueries extends boolean,
> = {
	<S extends TableAliasArg<Schema>>(
		table_alias: S,
		on: (b: ExpressionBuilder<Schema, A & ParseTableAlias<S, Schema>>) => Expression,
	): Stage<
		Schema,
		A & ParseTableAlias<S, Schema>,
		Left extends true ? LeftJoined | (keyof ParseTableAlias<S, Schema> & string) : LeftJoined,
		Row,
		AllowsTransactionRequiredQueries
	>
	<const Alias extends string, DerivedRow>(
		derived_table: DerivedTableArg<Alias, DerivedRow>,
		on: (b: ExpressionBuilder<DerivedSchema<Schema, Alias, DerivedRow>, DerivedAliasMap<Schema, A, Alias, DerivedRow>>) => Expression,
	): Stage<
		DerivedSchema<Schema, Alias, DerivedRow>,
		DerivedAliasMap<Schema, A, Alias, DerivedRow>,
		Left extends true ? LeftJoined | Alias : LeftJoined,
		Row,
		AllowsTransactionRequiredQueries
	>
}

type TransactionRequiredMethods<
	Schema extends SchemaColumnTypes,
	A extends AliasMap<Schema>,
	LeftJoined extends string,
	Row,
	Allows extends boolean,
> = Allows extends true
	? { for_update: () => Stage<Schema, A, LeftJoined, Row, Allows> }
	: {}

type Stage<
	Schema extends SchemaColumnTypes,
	A extends AliasMap<Schema>,
	LeftJoined extends string = never,
	Row = {},
	AllowsTransactionRequiredQueries extends boolean = false,
> = {
	join: Joiner<Schema, A, LeftJoined, Row, false, AllowsTransactionRequiredQueries>

	left_join: Joiner<Schema, A, LeftJoined, Row, true, AllowsTransactionRequiredQueries>

	where: (
		cb: (b: ExpressionBuilder<Schema, A>) => Expression,
	) => Stage<Schema, A, LeftJoined, Row, AllowsTransactionRequiredQueries>

	select: <const Exprs extends ReadonlyArray<SelectInput<Schema, A>>>(
		cb: (b: SelectExpressionBuilder<Schema, A>) => Exprs,
	) => Stage<Schema, A, LeftJoined, Row & RowFromSelectExprs<Schema, A, Exprs>, AllowsTransactionRequiredQueries>

	group_by: (...exprs: SelectColumnInput<Schema, A>[]) => Stage<Schema, A, LeftJoined, Row, AllowsTransactionRequiredQueries>

	order_by: (
		column: ColumnRef<Schema, A> | RowIdentifiers<Row> | ((b: ExpressionBuilder<Schema, A>) => FunctionExpression),
		direction?: OrderByDirection,
	) => Stage<Schema, A, LeftJoined, Row, AllowsTransactionRequiredQueries>

	having: (cb: (b: HavingExpressionBuilder<RowIdentifiers<Row>>) => Expression) => Stage<Schema, A, LeftJoined, Row, AllowsTransactionRequiredQueries>

	limit: (count: bigint) => Stage<Schema, A, LeftJoined, Row, AllowsTransactionRequiredQueries>

	build: () => BuiltQuery<FlattenRow<Row, LeftJoined>>
} & TransactionRequiredMethods<Schema, A, LeftJoined, Row, AllowsTransactionRequiredQueries>

export type QueryBuilder<Schema extends SchemaColumnTypes, AllowsTransactionRequiredQueries extends boolean = false> = {
	from: {
		<S extends TableAliasArg<Schema>>(
			table_alias: S,
		): Stage<Schema, ParseTableAlias<S, Schema>, never, {}, AllowsTransactionRequiredQueries>
		<const Alias extends string, Row>(
			derived_table: DerivedTableArg<Alias, Row>,
		): Stage<DerivedSchema<Schema, Alias, Row>, DerivedAliasMap<Schema, {}, Alias, Row>, never, {}, AllowsTransactionRequiredQueries>
	}
}

type ColumnOrValueInput = string | ValueRef | FunctionExpression

type BoolExpr = WhereGrouping | Comparison

type HavingBoolExpr = HavingGrouping | HavingComparison

type InternalSelectGrouping = SelectGrouping & { table_identifier: string; alias: string }

type State = {
	from: TableSource
	joins: TrustableJoin[]
	where_expressions: BoolExpr[]
	selects: Array<SelectExpression | InternalSelectGrouping>
	group_bys: SelectExpression[]
	order_bys: OrderBy[]
	havings: HavingBoolExpr[]
	limit: bigint | null
	for_update: boolean
	allow_transaction_required_queries: boolean
}

// Identifiers are interpolated straight into the SQL string (inside backticks), not parameterized,
// so the builder rejects anything outside a safe character set up front. This mirrors the assertion
// in safe_select_query_validator.ts — the validator is the security boundary on the server; this is the
// same rule enforced early for a clear error at construction time.
const assert_identifier = (s: string, role: string): string => {
	assert(/^\w+$/.test(s), `${role} must be a valid SQL identifier (letters, numbers, and underscores only): "${s}"`)
	return s
}

const parse_table_alias = (s: string): { table: string; alias: string } => {
	const m = /^(\w+)\s+AS\s+(\w+)$/i.exec(s)
	return m
		? { table: m[1]!, alias: m[2]! }
		: { table: assert_identifier(s, 'table name'), alias: assert_identifier(s, 'alias') }
}

const parse_col_ref = (s: string): { table: string; column: string } => {
	const dot = s.indexOf('.')
	return {
		table: assert_identifier(s.slice(0, dot), 'table identifier'),
		column: assert_identifier(s.slice(dot + 1), 'column'),
	}
}

const to_select_expression = (input: string | SelectableFunctionExpression): SelectExpression => {
	if (typeof input !== 'string') return input
	const m = /^(\w+)\.(\w+)(?:\s+AS\s+(\w+))?$/i.exec(input)
	if (!m) throw new Error(`invalid select column: ${input}`)
	const base = { type: 'column reference' as const, table_identifier: m[1]!, column: m[2]! }
	return m[3] !== undefined ? { ...base, alias: m[3] } : base
}

const to_column_or_value = (input: ColumnOrValueInput): ColumnReference | UserProvidedValue | FunctionExpression => {
	if (typeof input === 'object' && 'type' in input && input.type === 'function') return input
	if (typeof input === 'object' && 'value' in input) {
		return { type: 'user provided value', value: input.value }
	}
	const { table, column } = parse_col_ref(input as string)
	return { type: 'column reference', table_identifier: table, column }
}

const array_comparison = (left: string | FunctionExpression, comparator: ArrayComparator, values: ArrayValues): BoolExpr => {
	assert(values.length > 0, `an ${comparator} array has at least one value`)
	const left_operand = to_column_or_value(left)
	assert(left_operand.type !== 'user provided value', `the left side of ${comparator} is a column or a function`)
	return {
		type: 'comparison',
		left: left_operand,
		comparator,
		right: { type: 'user provided value array', values: [...values] },
	}
}

const expression_builder = {
	comparison: (left: ColumnOrValueInput, comparator: Comparator, right: ColumnOrValueInput): BoolExpr => ({
		type: 'comparison',
		left: to_column_or_value(left),
		comparator,
		right: to_column_or_value(right),
	}),
	in: (left: string | FunctionExpression, values: ArrayValues): BoolExpr => array_comparison(left, 'IN', values),
	not_in: (left: string | FunctionExpression, values: ArrayValues): BoolExpr => array_comparison(left, 'NOT IN', values),
	and: (...exprs: BoolExpr[]): BoolExpr => ({ type: 'and', expressions: exprs }),
	or: (...exprs: BoolExpr[]): BoolExpr => ({ type: 'or', expressions: exprs }),
	fn: (name: FunctionName, ...args: ColumnOrValueInput[]): FunctionExpression => ({
		type: 'function',
		function: name,
		arguments: args.map(a => {
			const v = to_column_or_value(a)
			if (v.type === 'function') throw new Error('nested function expressions are not supported')
			return v
		}),
	}),
}

const to_alias_or_value = (input: AliasOrValueInput): AliasReference | UserProvidedValue =>
	typeof input === 'object'
		? { type: 'user provided value', value: input.value }
		// Alias references are typed as the finite union of selected aliases (RowIdentifiers), so the
		// type system already constrains them; the validator is the authoritative runtime guard.
		: { type: 'alias reference', alias: input }

type AliasOrValueInput = string | ValueRef

const having_expression_builder = {
	comparison: (left: AliasOrValueInput, comparator: Comparator, right: AliasOrValueInput): HavingBoolExpr => ({
		type: 'comparison',
		left: to_alias_or_value(left),
		comparator,
		right: to_alias_or_value(right),
	}),
	and: (...exprs: HavingBoolExpr[]): HavingBoolExpr => ({ type: 'and', expressions: exprs }),
	or: (...exprs: HavingBoolExpr[]): HavingBoolExpr => ({ type: 'or', expressions: exprs }),
}

const having_bool_expr_to_grouping = (expr: HavingBoolExpr): HavingGrouping =>
	'expressions' in expr
		? expr
		: { type: 'and' as const, expressions: [expr] }

const to_select_grouping_expression = (item: string | SelectGrouping): SelectExpression | SelectGrouping =>
	typeof item === 'string' ? to_select_expression(item) : item

const parse_select_alias = (alias: string, role: string): { table_identifier: string; alias: string } => {
	const [table_identifier, col_alias, ...rest] = alias.split('.')
	assert(table_identifier && col_alias && rest.length === 0, `${role} alias must be "table.col_alias": ${alias}`)
	return {
		table_identifier: assert_identifier(table_identifier, `${role} table identifier`),
		alias: assert_identifier(col_alias, `${role} alias`),
	}
}

const split_trailing_alias = <T>(rest: (T | string)[], role: string): { items: (T | string)[]; alias: string } => {
	const alias = rest.at(-1)
	assert(typeof alias === 'string', `the last argument to ${role} is the alias string`)
	return { items: rest.slice(0, -1), alias }
}

const select_expression_builder = {
	fn: (name: FunctionName, ...rest: (string | ValueRef)[]): SelectableFunctionExpression => {
		const { items: fn_args, alias } = split_trailing_alias(rest, 'select fn')
		if (fn_args.length > 2) throw new Error('fn supports at most 2 arguments')
		const args = map(fn_args, arg => {
			if (typeof arg === 'object') {
				return { type: 'user provided value' as const, value: arg.value }
			}
			const { table, column } = parse_col_ref(arg)
			return { type: 'column reference' as const, table_identifier: table, column }
		})
		return { type: 'function', function: name, arguments: args, ...parse_select_alias(alias, 'select fn') }
	},
	and: (...rest: (string | SelectGrouping)[]): InternalSelectGrouping => {
		const { items, alias } = split_trailing_alias(rest, 'select grouping')
		return { type: 'and', expressions: map(items, to_select_grouping_expression), ...parse_select_alias(alias, 'select grouping') }
	},
	or: (...rest: (string | SelectGrouping)[]): InternalSelectGrouping => {
		const { items, alias } = split_trailing_alias(rest, 'select grouping')
		return { type: 'or', expressions: map(items, to_select_grouping_expression), ...parse_select_alias(alias, 'select grouping') }
	},
}

const bool_expr_to_where_grouping = (expr: BoolExpr): WhereGrouping =>
	'expressions' in expr
		? expr
		: { type: 'and' as const, expressions: [expr] }

const to_source = (
	arg: string | DerivedTableArg<string, unknown>,
	allow_transaction_required_queries: boolean,
): TableSource => {
	if (typeof arg === 'string') {
		const { table, alias } = parse_table_alias(arg)
		return { table_name: table, alias }
	}
	const derived_query = arg.subquery.query
	assert(
		allow_transaction_required_queries
			|| derived_query === undefined
			|| !query_requires_transaction(derived_query),
		'Derived table query requires a transaction-enabled query builder',
	)
	return { subquery: derived_query, alias: assert_identifier(arg.alias, 'derived table alias') }
}

const joiner = ({ left, state }: { left: boolean, state: State }) => (arg: string | DerivedTableArg<string, unknown>, on: (b: typeof expression_builder) => BoolExpr) => {
	const expr = on(expression_builder)
	return make_stage({
		...state,
		joins: [...state.joins, { ...to_source(arg, state.allow_transaction_required_queries), on_clause: [expr], left }],
	})
}

const make_stage = (state: State): any => ({
	join: joiner({ left: false, state }),
	left_join: joiner({ left: true, state }),
	where: (cb: (b: typeof expression_builder) => BoolExpr) => {
		return make_stage({ ...state, where_expressions: [...state.where_expressions, cb(expression_builder)] })
	},
	select: (cb: (b: typeof select_expression_builder) => ReadonlyArray<string | SelectableFunctionExpression | SelectGrouping>) => {
		const items = cb(select_expression_builder)
		const new_selects = map(items, (item): SelectExpression | InternalSelectGrouping => {
			if (typeof item === 'string') return to_select_expression(item)
			if (item.type === 'and' || item.type === 'or') return item as InternalSelectGrouping
			assert(item.type === 'function', `expected function expression in select, got "${item.type}"`)
			return to_select_expression(item)
		})
		return make_stage({ ...state, selects: [...state.selects, ...new_selects] })
	},
	group_by: (...exprs: string[]) => {
		return make_stage({ ...state, group_bys: [...state.group_bys, ...map(exprs, to_select_expression)] })
	},
	order_by: (column: string | ((b: typeof expression_builder) => FunctionExpression), direction: OrderByDirection = 'ASC') => {
		let expression: OrderByExpression
		if (typeof column === 'function') {
			expression = column(expression_builder)
		} else if (column.includes('.')) {
			const { table, column: column_name } = parse_col_ref(column)
			expression = { type: 'column reference', table_identifier: table, column: column_name }
		} else {
			expression = { type: 'alias reference', alias: column }
		}
		const order: OrderBy = { expression, direction }
		return make_stage({ ...state, order_bys: [...state.order_bys, order] })
	},
	having: (cb: (b: typeof having_expression_builder) => HavingBoolExpr) => {
		return make_stage({ ...state, havings: [...state.havings, cb(having_expression_builder)] })
	},
	limit: (count: bigint) => {
		return make_stage({ ...state, limit: count })
	},
	for_update: () => {
		assert(state.allow_transaction_required_queries, 'FOR UPDATE requires a transaction-enabled query builder')
		return make_stage({ ...state, for_update: true })
	},
	build: (): BuiltQuery<unknown> => {
		const response_columns: ResponseColumn[] = map(state.selects, s => {
			if ('expressions' in s) {
				return { table_identifier: s.table_identifier, name: s.alias }
			}

			const name = s.type === 'column reference' ? (s.alias ?? s.column) : s.alias
			return { table_identifier: s.table_identifier, name }
		})
		return {
			query: {
				select: map(state.selects, (s): SelectExpression | SelectGrouping => {
					if ('expressions' in s) {
						return omit(s, ['table_identifier'])
					}

					return s
				}),
				from: state.from,
				joins: state.joins,
				where: state.where_expressions.length === 0
					? null
					: state.where_expressions.length === 1
						? bool_expr_to_where_grouping(state.where_expressions[0]!)
						: { type: 'and' as const, expressions: state.where_expressions },
				group_by: state.group_bys,
				order_by: state.order_bys,
				limit: state.limit,
				having: state.havings.length === 0
					? null
					: state.havings.length === 1
						? having_bool_expr_to_grouping(state.havings[0]!)
						: { type: 'and' as const, expressions: state.havings },
				...(state.for_update ? { for_update: true } : {}),
			},
			response_columns,
			positional_row_to_named: (row: unknown[]): Record<string, Record<string, unknown>> => {
				const results: Record<string, Record<string, unknown>> = {}

				for_each(response_columns, (response_column, index) => {
					results[response_column.table_identifier] = {
						...results[response_column.table_identifier],
						[response_column.name]: row[index],
					}
				})

				return results
			}
		}
	},
})

function query_builder<Schema extends SchemaColumnTypes>(): QueryBuilder<Schema, false>
function query_builder<Schema extends SchemaColumnTypes>(
	options: { allow_transaction_required_queries?: false },
): QueryBuilder<Schema, false>
function query_builder<Schema extends SchemaColumnTypes>(
	options: { allow_transaction_required_queries: true },
): QueryBuilder<Schema, true>
function query_builder<Schema extends SchemaColumnTypes>(
	options: { allow_transaction_required_queries?: boolean } = {},
): QueryBuilder<Schema, boolean> {
	const allow_transaction_required_queries = options.allow_transaction_required_queries === true
	return {
		from: ((arg: string | DerivedTableArg<string, unknown>) => {
			return make_stage({
				from: to_source(arg, allow_transaction_required_queries),
				joins: [],
				where_expressions: [],
				selects: [],
				group_bys: [],
				order_bys: [],
				havings: [],
				limit: null,
				for_update: false,
				allow_transaction_required_queries,
			})
		}) as any,
	}
}

export default query_builder
